import { useMemo } from 'react';

import { useGetContrato } from '@/shared/actions/erp';
import {
  useBuscarClientes,
  useVision360Cliente,
} from '@/shared/actions/erp/tickets-soporte-cliente';
import {
  IClienteBusqueda,
  IVision360Data,
  IVision360Equipo,
} from '@/shared/interfaces/erp/tickets-soporte-clientes';

/** Una ONT que el cliente ya tiene registrada en tickets. */
export type CandidatoOnt = {
  serie: string;
  modelo: string;
  observaciones?: string;
  fecha_sistema?: string;
};

/**
 * ONTs que el cliente YA tiene, leídas de tickets (Datos Técnicos → Equipos).
 *
 * Reusa el mismo puente que la tarjeta de Visión 360 del traslado
 * (contrato → cédula → cliente de tickets → visión 360), así que en esta
 * pantalla las consultas ya están en caché de react-query y no se repite
 * ninguna petición.
 */
export function useOntClienteCandidatos(ordenTrabajo: any, enabled = true) {
  const idContrato: string | undefined =
    ordenTrabajo?.contrato_data?.id_contrato || ordenTrabajo?.contrato;

  const { data: contratoRes, isLoading: cargandoContrato } = useGetContrato(
    idContrato || '',
    !!idContrato && enabled,
  );
  const contrato: any = (contratoRes as any)?.data ?? null;

  const cedula: string =
    contrato?.cliente_data?.identificacion ||
    ordenTrabajo?.prospecto_data?.identificacion ||
    '';
  const numeroReferencia: string =
    contrato?.numero_referencia || contrato?.codigo || '';

  const { data: buscarRes, isLoading: cargandoBusqueda } = useBuscarClientes({
    query: cedula,
    enabled: enabled && !!cedula && cedula.length >= 3,
  });

  const cliente = useMemo<IClienteBusqueda | null>(() => {
    const lista: IClienteBusqueda[] =
      ((buscarRes?.data as any)?.data as IClienteBusqueda[]) ||
      (buscarRes?.data as any) ||
      [];
    const arr = Array.isArray(lista) ? lista : [];
    // Mismo criterio que la Visión 360: se prefiere el cliente cuyo contrato
    // coincide con el del ERP; si no, el primero.
    const exacto = arr.find(
      (c) =>
        c?.contrato_codigo &&
        numeroReferencia &&
        String(c.contrato_codigo) === String(numeroReferencia),
    );
    return exacto || arr[0] || null;
  }, [buscarRes, numeroReferencia]);

  const { data: visionRes, isLoading: cargandoVision } = useVision360Cliente(
    cliente?.id || '',
    cliente?.contrato_id || undefined,
    enabled && !!cliente?.id,
  );

  const vision: IVision360Data | null =
    ((visionRes?.data as any)?.data as IVision360Data) ||
    (visionRes?.data as any) ||
    null;

  const candidatos = useMemo<CandidatoOnt[]>(() => {
    const contratos: any[] = Array.isArray(vision?.contratos)
      ? (vision?.contratos as any[])
      : [];
    const equipos: IVision360Equipo[] = contratos.flatMap((c) =>
      Array.isArray(c?.equipos) ? (c.equipos as IVision360Equipo[]) : [],
    );

    const vistas = new Set<string>();
    return equipos
      .filter((e) => {
        // El "ONT" puede venir en el modelo, en la marca o en el campo equipo.
        // En los datos reales la marca llega vacía y el texto está en el
        // modelo ("ONT HUAWEI EG8145X6-10", "HUAWEI ONT HG8310M 1ETHER").
        const texto = `${e?.marca || ''} ${e?.modelo || ''} ${e?.equipo || ''}`;
        return texto.toUpperCase().includes('ONT');
      })
      .filter((e) => {
        const serie = (e?.serie_mac || e?.serie_num || '').trim();
        if (!serie) return false;
        const clave = serie.toUpperCase();
        if (vistas.has(clave)) return false;
        vistas.add(clave);
        return true;
      })
      .map((e) => ({
        serie: (e?.serie_mac || e?.serie_num || '').trim(),
        modelo: (e?.modelo || e?.marca || '').trim(),
        observaciones: e?.observaciones,
        fecha_sistema: e?.fecha_sistema,
      }));
  }, [vision]);

  return {
    candidatos,
    cedula,
    contratoSoftnet: cliente?.contrato_codigo || null,
    isLoading: cargandoContrato || cargandoBusqueda || cargandoVision,
    // Sirve para distinguir "el cliente no tiene ONT registrada" de "no se
    // pudo llegar a tickets".
    tieneDatosDeTickets: !!vision,
  };
}
